import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { getMeetingPublicPublicMeetingPublicIdGet } from '@/api';
import RoomScene from '@/scenes/Room/RoomScene';
import meetingCss from '@/styles/meeting.css?url';
import Loader from '@/components/Loader';

export const Route = createFileRoute('/meeting/$roomPublicId/')({
  component: RouteComponent,
  validateSearch: z.object({
    debug: z.coerce.boolean().optional(),
    test: z.coerce.boolean().optional(),
    templateId: z.string().optional(),
    imageDemo: z.coerce.boolean().optional(),
  }),
  loaderDeps: ({ search }) => ({ imageDemo: search.imageDemo }),
  loader: async ({ params, deps }) => {
    if (deps.imageDemo) {
      return { id: 'demo', public_id: params.roomPublicId } as any;
    }
    const room = await getMeetingPublicPublicMeetingPublicIdGet({
      path: { public_id: params.roomPublicId },
    });
    return room.data;
  },
  head: ({ loaderData, params }) => {
    const room = loaderData;
    const roomLink = `https://app.greetai.co/meeting/${params.roomPublicId}`;

    return {
      title: 'Session',
      meta: [
        {
          name: 'title',
          property: 'og:title',
          content: 'Session',
        },
        {
          name: 'description',
          property: 'og:description',
          content: 'Session',
        },
        {
          property: 'og:url',
          content: roomLink,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          property: 'twitter:domain',
          content: 'greetai.co',
        },
        {
          property: 'twitter:url',
          content: roomLink,
        },
      ],
      links: [
        {
          rel: 'stylesheet',
          href: meetingCss,
        },
      ],
    };
  },
});

function RouteComponent() {
  const room = Route.useLoaderData();
  const { debug, test, templateId } = Route.useSearch();

  // Not in meeting.css: route stylesheets stay in <head> after leaving the route, so a body rule there leaks to every page after.
  useEffect(() => {
    document.body.style.backgroundColor = '#333';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  if (!room) {
    return <Loader />;
  }

  return <RoomScene isTest={test} testTemplateId={templateId} debug={debug} />;
}
